import { useNavigate } from "react-router-dom";
import SidePanel from "./SidePanel";
import CartItemRow from "./CartItemRow";
import { useStore } from "@/store/StoreContext";
import { formatBRL } from "@/lib/format";

interface CartPanelProps {
  open: boolean;
  onClose: () => void;
}

export default function CartPanel({ open, onClose }: CartPanelProps) {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, subtotal, discount, total, cartCount } =
    useStore();

  const goToCart = () => {
    onClose();
    navigate("/checkout");
  };

  return (
    <SidePanel
      open={open}
      title="Sua sacola"
      subtitle={
        cartCount > 0
          ? `${cartCount} ${cartCount === 1 ? "produto" : "produtos"} selecionados`
          : "Escolha seus produtos favoritos com calma."
      }
      onClose={onClose}
    >
      {cart.length === 0 ? (
        <div className="flex flex-col items-center py-14 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-50 text-primary-600">
            <i className="ri-shopping-bag-3-line text-3xl" />
          </span>
          <p className="mt-5 font-heading text-xl text-foreground-900">Sua sacola está vazia</p>
          <p className="mt-2 max-w-xs text-sm text-foreground-600">
            Escolha seus produtos favoritos e volte aqui para finalizar seu pedido.
          </p>
          <button
            type="button"
            onClick={() => {
              onClose();
              navigate("/novidades");
            }}
            className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            Ver produtos
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {cart.map((item) => (
            <CartItemRow
              key={item.key}
              item={item}
              onChangeQuantity={updateQuantity}
              onRemove={removeFromCart}
              onNavigate={onClose}
            />
          ))}

          <div className="mt-2 rounded-lg border border-background-200 bg-background-100 p-4">
            <div className="flex items-center justify-between text-sm text-foreground-700">
              <span>Subtotal</span>
              <span>{formatBRL(subtotal)}</span>
            </div>
            {discount > 0 ? (
              <div className="mt-2 flex items-center justify-between text-sm text-accent-700">
                <span>Desconto demonstrativo</span>
                <span>-{formatBRL(discount)}</span>
              </div>
            ) : null}
            <div className="mt-3 flex items-center justify-between border-t border-background-300 pt-3">
              <span className="font-heading text-xl text-foreground-900">Total estimado</span>
              <span className="font-heading text-xl text-primary-700">{formatBRL(total)}</span>
            </div>
          </div>

          <button
            type="button"
            onClick={goToCart}
            className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-whatsapp-line text-lg" />
            Finalizar pedido pelo WhatsApp
          </button>
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer text-center text-sm text-foreground-600 underline underline-offset-4 transition-colors hover:text-primary-600"
          >
            Continuar escolhendo
          </button>
        </div>
      )}
    </SidePanel>
  );
}